import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useAuthStore } from "@store/auth";
import { useDataStore } from "@store/user_org";
import {
  isGUEST,
  isDESSINATEUR,
  canSeeSuiviSalaries,
  cannotSeeFacturation,
} from "@helpers/accessHelpers";
import ComponentsScreen from "@screens/ComponentsScreen";
import LoginScreen from "@screens/auth/LoginScreen";
import AcceptInvitationScreen from "@screens/auth/AcceptInvitationScreen";
import RepairAccountScreen from "@screens/auth/RepairAccountScreen";
import ResetPasswordScreen from "@screens/auth/ResetPasswordScreen";
import SideBar from "./SideBar";
import { ErrorProvider } from "context/ErrorContext";
import { NavigationProvider } from "context/NavigationContext";
import { NotificationProvider } from "context/NotificationContext";
import SettingsScreen from "@screens/SettingsScreen";
import ProjectsScreen from "@screens/ProjectsScreen";
import ProjectScreen from "@screens/ProjectScreen";
import PartnersScreen from "@screens/PartnersScreen";
import ClientArtisansScreen from "@screens/ClientArtisansScreen";
import BroadcastsScreen from "@screens/BroadcastsScreen";
import EmailsSentScreen from "@screens/EmailsSentScreen";
import HomeScreen from "@screens/HomeScreen";
import SignScreen from "@screens/SignScreen";
import BibliothequeScreen from "@screens/BibliothequeScreen";
import TicketsScreen from "@screens/TicketsScreen";
import FacturationScreen from "@screens/FacturationScreen";
import SuiviSalariesScreen from "@screens/SuiviSalariesScreen";
import { MobileAppFloatingButton } from "@components/ui/MobileAppFloatingButton";
import { MobileAppBanner } from "@components/ui/MobileAppBanner";

function MainLayout() {
  return (
    <div className="flex flex-row overflow-x-hidden">
      <SideBar />
      <div className="flex-1 min-w-0">
        <Outlet /> {/* Les routes enfants s'affichent ici */}
      </div>
      <MobileAppFloatingButton />
    </div>
  );
}

export default function Navigation() {
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const userData = useDataStore((s) => s.userData);
  const needsAccountRepair = useDataStore((s) => s.needsAccountRepair);
  const isGuest = isGUEST(userData);
  const isDessinateur = isDESSINATEUR(userData);

  if (loading) {
    return <div className="bg-neutral-50 h-screen w-screen" />; // Loading state
  }

  // Firebase says we have a user but the Firestore profile isn't in the
  // store yet (fresh login, backend hiccup, refresh after idle...). Render
  // only a loading placeholder for the main layout, but DON'T unmount
  // BrowserRouter / NavigationProvider — doing so re-registers the global
  // navigation handler with a stale closure and can make sidebar clicks
  // silently hit the old one. Public routes (sign, accept-invitation)
  // still render fine through the router.
  const authedButNoProfile = !!user && !userData;

  return (
    <div className="bg-neutral-50">
      <ErrorProvider>
        <NotificationProvider>
          <BrowserRouter>
            <NavigationProvider>
              <MobileAppBanner authed={!!user && !!userData} />
              <Routes>
                {/* Public routes — accessible without authentication */}
                <Route path="/sign/:token" element={<SignScreen />} />
                <Route path="/accept-invitation" element={<AcceptInvitationScreen />} />
                <Route path="/reset-password" element={<ResetPasswordScreen />} />
                {needsAccountRepair ? (
                  // Authenticated but no backend profile (orphan account):
                  // self-service repair screen instead of a silent logout.
                  <Route path="*" element={<RepairAccountScreen />} />
                ) : authedButNoProfile ? (
                  <Route
                    path="*"
                    element={<div className="bg-neutral-50 h-screen w-screen" />}
                  />
                ) : user ? (
                  <>
                    <Route element={<MainLayout />}>
                      <Route
                        path="/"
                        element={<Navigate to={isGuest ? "/projects" : "/home"} replace />}
                      />
                      <Route
                        path="/home"
                        element={isGuest ? <Navigate to="/projects" replace /> : <HomeScreen />}
                      />
                      <Route path="/projects" element={<ProjectsScreen />} />
                      <Route path="/project/:id" element={<ProjectScreen />} />
                      <Route
                        path="/partners"
                        element={
                          isGuest
                            ? <ClientArtisansScreen />
                            : isDessinateur
                              ? <Navigate to="/projects" replace />
                              : <PartnersScreen />
                        }
                      />
                      <Route
                        path="/broadcasts"
                        element={isGuest ? <Navigate to="/projects" replace /> : <BroadcastsScreen />}
                      />
                      <Route
                        path="/emails-sent"
                        element={isGuest ? <Navigate to="/projects" replace /> : <EmailsSentScreen />}
                      />
                      <Route
                        path="/inbox"
                        element={<Navigate to="/settings?page=inbox" replace />}
                      />
                      <Route
                        path="/bibliotheque"
                        element={isGuest ? <BibliothequeScreen /> : <Navigate to="/settings?page=library" replace />}
                      />
                      <Route
                        path="/facturation"
                        element={isGuest || cannotSeeFacturation(userData) ? <Navigate to="/projects" replace /> : <FacturationScreen />}
                      />
                      <Route
                        path="/suivi-salaries"
                        element={canSeeSuiviSalaries(userData) ? <SuiviSalariesScreen /> : <Navigate to="/projects" replace />}
                      />
                      <Route path="/settings" element={<SettingsScreen />} />
                      <Route
                        path="/tickets"
                        element={isGuest ? <Navigate to="/projects" replace /> : <TicketsScreen />}
                      />
                      <Route
                        path="/components"
                        element={isGuest ? <Navigate to="/projects" replace /> : <ComponentsScreen />}
                      />
                      <Route
                        path="*"
                        element={<Navigate to={isGuest ? "/projects" : "/home"} replace />}
                      />
                    </Route>
                  </>
                ) : (
                  <>
                    <Route path="/login" element={<LoginScreen />} />
                    <Route
                      path="*"
                      element={<Navigate to="/login" replace />}
                    />
                  </>
                )}
              </Routes>
            </NavigationProvider>
          </BrowserRouter>
        </NotificationProvider>
      </ErrorProvider>
    </div>
  );
}
